/**
 * Expense Statistics Service
 *
 * Handles all statistical and analytical operations for expenses.
 * This service provides aggregated data, metrics, and reports.
 */

import { RepositoryResult } from './BaseRepository';
import { getDbClient } from '@/server/lib/database';

/**
 * Expense summary for dashboard
 */
export type ExpenseSummary = {
  total: number;
  byCategory: Record<string, number>;
  billable: number;
  nonBillable: number;
  currentMonth: number;
  previousMonth: number;
};

/**
 * Expense performance metrics
 */
export type ExpensePerformanceMetrics = {
  averageExpense: number;
  highestExpense: number;
  lowestExpense: number;
  totalByCategory: Record<string, number>;
  mostExpensiveCategory: string;
  billablePercentage: number;
};

/**
 * Expense trends over time
 */
export type ExpenseTrend = {
  month: string;
  year: number;
  total: number;
  count: number;
};

/**
 * Expense Statistics Service
 * Provides statistical and analytical operations for expenses
 */
export class ExpenseStatisticsService {
  private tableName = 'expenses' as const;

  /**
   * Get expense summary for dashboard
   */
  async getSummary(userId: string): Promise<RepositoryResult<ExpenseSummary>> {
    try {
      const db = getDbClient();

      const { data, error } = await db
        .from(this.tableName)
        .select('*')
        .eq('user_id', userId);

      if (error) {
        throw error;
      }

      const expenses = data || [];

      // Calculate totals
      const total = expenses.reduce((sum, exp) => sum + (exp.amount || 0), 0);

      // Group by category
      const byCategory: Record<string, number> = {};
      expenses.forEach((exp) => {
        const category = exp.category || 'Other';
        byCategory[category] = (byCategory[category] || 0) + (exp.amount || 0);
      });

      // Billable vs non-billable
      const billable = expenses
        .filter((exp) => exp.is_billable)
        .reduce((sum, exp) => sum + (exp.amount || 0), 0);

      const nonBillable = total - billable;

      // Current month vs previous month
      const now = new Date();
      const currentMonth = now.getMonth();
      const currentYear = now.getFullYear();

      const previousMonth = currentMonth === 0 ? 11 : currentMonth - 1;
      const previousMonthYear = currentMonth === 0 ? currentYear - 1 : currentYear;

      const currentMonthExpenses = expenses.filter((exp) => {
        const expDate = new Date(exp.date);
        return expDate.getMonth() === currentMonth && expDate.getFullYear() === currentYear;
      });

      const previousMonthExpenses = expenses.filter((exp) => {
        const expDate = new Date(exp.date);
        return expDate.getMonth() === previousMonth && expDate.getFullYear() === previousMonthYear;
      });

      const currentMonthTotal = currentMonthExpenses.reduce((sum, exp) => sum + (exp.amount || 0), 0);
      const previousMonthTotal = previousMonthExpenses.reduce((sum, exp) => sum + (exp.amount || 0), 0);

      return {
        success: true,
        data: {
          total,
          byCategory,
          billable,
          nonBillable,
          currentMonth: currentMonthTotal,
          previousMonth: previousMonthTotal,
        },
      };
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'fetching expense summary'),
      };
    }
  }

  /**
   * Get expense performance metrics
   */
  async getPerformanceMetrics(userId: string): Promise<RepositoryResult<ExpensePerformanceMetrics>> {
    try {
      const db = getDbClient();

      const { data, error } = await db
        .from(this.tableName)
        .select('amount, category, is_billable')
        .eq('user_id', userId);

      if (error) {
        throw error;
      }

      const expenses = data || [];

      if (expenses.length === 0) {
        return {
          success: true,
          data: {
            averageExpense: 0,
            highestExpense: 0,
            lowestExpense: 0,
            totalByCategory: {},
            mostExpensiveCategory: 'N/A',
            billablePercentage: 0,
          },
        };
      }

      // Calculate average, highest, lowest
      const amounts = expenses.map((exp) => exp.amount || 0);
      const averageExpense = amounts.reduce((sum, amt) => sum + amt, 0) / amounts.length;
      const highestExpense = Math.max(...amounts);
      const lowestExpense = Math.min(...amounts);

      // Total by category
      const totalByCategory: Record<string, number> = {};
      expenses.forEach((exp) => {
        const category = exp.category || 'Other';
        totalByCategory[category] = (totalByCategory[category] || 0) + (exp.amount || 0);
      });

      // Most expensive category
      const mostExpensiveCategory = Object.entries(totalByCategory).sort((a, b) => b[1] - a[1])[0]?.[0] || 'N/A';

      // Billable percentage
      const billableExpenses = expenses.filter((exp) => exp.is_billable);
      const billablePercentage = (billableExpenses.length / expenses.length) * 100;

      return {
        success: true,
        data: {
          averageExpense,
          highestExpense,
          lowestExpense,
          totalByCategory,
          mostExpensiveCategory,
          billablePercentage,
        },
      };
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'fetching expense performance metrics'),
      };
    }
  }

  /**
   * Get expense trends over time
   */
  async getExpenseTrends(
    userId: string,
    months: number = 12
  ): Promise<RepositoryResult<ExpenseTrend[]>> {
    try {
      const db = getDbClient();

      const startDate = new Date();
      startDate.setMonth(startDate.getMonth() - months);

      const { data, error } = await db
        .from(this.tableName)
        .select('amount, date')
        .eq('user_id', userId)
        .gte('date', startDate.toISOString());

      if (error) {
        throw error;
      }

      const expenses = data || [];

      // Group by month
      const monthlyData = new Map<string, { total: number; count: number }>();

      expenses.forEach((exp) => {
        const date = new Date(exp.date);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

        const current = monthlyData.get(monthKey) || { total: 0, count: 0 };
        current.total += exp.amount || 0;
        current.count += 1;
        monthlyData.set(monthKey, current);
      });

      // Convert to array
      const trends = Array.from(monthlyData.entries()).map(([key, data]) => {
        const [year, month] = key.split('-');
        const date = new Date(parseInt(year), parseInt(month) - 1);

        return {
          month: date.toLocaleString('default', { month: 'short' }),
          year: parseInt(year),
          total: data.total,
          count: data.count,
        };
      });

      return {
        success: true,
        data: trends,
      };
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'fetching expense trends'),
      };
    }
  }

  /**
   * Get expenses by category with totals
   */
  async getExpensesByCategory(
    userId: string,
    category?: string
  ): Promise<RepositoryResult<Array<{ category: string; total: number; count: number }>>> {
    try {
      const db = getDbClient();

      let query = db
        .from(this.tableName)
        .select('category, amount')
        .eq('user_id', userId);

      if (category) {
        query = query.eq('category', category);
      }

      const { data, error } = await query;

      if (error) {
        throw error;
      }

      const expenses = data || [];

      // Group by category
      const categoryData = new Map<string, { total: number; count: number }>();

      expenses.forEach((exp) => {
        const cat = exp.category || 'Other';
        const current = categoryData.get(cat) || { total: 0, count: 0 };
        current.total += exp.amount || 0;
        current.count += 1;
        categoryData.set(cat, current);
      });

      // Convert to array
      const result = Array.from(categoryData.entries()).map(([category, data]) => ({
        category,
        total: data.total,
        count: data.count,
      }));

      return {
        success: true,
        data: result,
      };
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'fetching expenses by category'),
      };
    }
  }

  /**
   * Get billable vs non-billable breakdown
   */
  async getBillableBreakdown(userId: string): Promise<RepositoryResult<{
    billable: number;
    nonBillable: number;
    billableCount: number;
    nonBillableCount: number;
  }>> {
    try {
      const db = getDbClient();

      const { data, error } = await db
        .from(this.tableName)
        .select('amount, is_billable')
        .eq('user_id', userId);

      if (error) {
        throw error;
      }

      const expenses = data || [];

      let billable = 0;
      let nonBillable = 0;
      let billableCount = 0;
      let nonBillableCount = 0;

      expenses.forEach((exp) => {
        const amount = exp.amount || 0;
        if (exp.is_billable) {
          billable += amount;
          billableCount += 1;
        } else {
          nonBillable += amount;
          nonBillableCount += 1;
        }
      });

      return {
        success: true,
        data: {
          billable,
          nonBillable,
          billableCount,
          nonBillableCount,
        },
      };
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'fetching billable breakdown'),
      };
    }
  }

  /**
   * Helper method to handle errors consistently
   */
  private handleError(error: any, context: string): string {
    console.error(`Error ${context}:`, error);
    return error?.message || `Failed to ${context}`;
  }
}

// Export singleton instance
export const expenseStatisticsService = new ExpenseStatisticsService();
